import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";

import DemandesList from "./components/DemandesList";
import DemandeDetail from "./components/DemandeDetail";
import ImportExcel from "./components/ImportExcel";

import OperatorMenu from "./components/OperatorMenu";
import OperateurItemPage from "./components/OperateurItemPage";
import StatsPage from "./components/StatsPage";

import "./App.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

function formatFrDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR");
}

function formatFrDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR");
}

/**
 * ✅ Signature MIH affichée sur toutes les pages
 * Met ton fichier dans: /public/mih-logo.png
 */
function SiteSignature() {
  const src = `${process.env.PUBLIC_URL || ""}/mih-logo.png`;

  return (
    <div
      style={{
        position: "fixed",
        right: -52,
        bottom: 10,
        zIndex: 9999,
        opacity: 0.55,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <img
        src={src}
        alt="MIH"
        style={{ height: 190, width: "auto", display: "block" }}
      />
    </div>
  );
}

function Home({ viewerName }) {
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // recherche accueil (déjà en place)
  const [searchText, setSearchText] = useState("");

  // ✅ compteur demandes sans opérateur
  const [unassignedCount, setUnassignedCount] = useState(0);

  // ✅ AJOUT : délai de traitement (hors EACI)
  const [delayDays, setDelayDays] = useState(0);
  const [delayFrom, setDelayFrom] = useState(null);

  // ✅ AJOUT : dernier import (qui + quand)
  const [lastImportBy, setLastImportBy] = useState("");
  const [lastImportAt, setLastImportAt] = useState(null);

  const refreshList = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const fetchLastImport = async () => {
      try {
        const res = await fetch(`${API}/api/meta/last-import`);
        if (!res.ok) return;

        const data = await res.json();
        setLastImportBy((data?.last_import_by || "").toString());
        setLastImportAt(data?.last_import_at || null);
      } catch (e) {
        // silencieux
      }
    };

    fetchLastImport();
  }, [refreshKey]);

  return (
    <div className="content">
      <h1>Suivi des demandes SIAO</h1>

      <div className="list-wrapper">
        {!selectedDemande ? (
          <>
            <div className="import-zone">
              <ImportExcel onImportSuccess={refreshList} viewerName={viewerName} />

              {/* ✅ affiche le dernier import */}
              <div style={{ marginTop: 8, opacity: 0.85, fontWeight: 800 }}>
                Dernier import :{" "}
                {lastImportAt ? (
                  <>
                    {lastImportBy ? lastImportBy : "—"} —{" "}
                    {formatFrDateTime(lastImportAt)}
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Rechercher (nom, prénom, numéro)…"
              style={{
                width: "100%",
                maxWidth: 520,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #ddd",
                margin: "10px 0 14px",
              }}
            />

            {/* ✅ affichage au-dessus de la liste */}
            <div style={{ marginBottom: 10, fontWeight: 900 }}>
              Demandes sans opérateur : {unassignedCount}
            </div>

            {/* ✅ AJOUT : délai de traitement */}
            <div style={{ marginBottom: 10, fontWeight: 900 }}>
              Délai de traitement actuel (hors EACI) : {delayDays} jours
              {delayFrom ? (
                <span style={{ fontWeight: 800, opacity: 0.8 }}>
                  {" "}
                  (plus ancienne : {formatFrDate(delayFrom)})
                </span>
              ) : null}
            </div>

            <DemandesList
              key={refreshKey}
              refreshKey={refreshKey}
              onSelect={setSelectedDemande}
              searchText={searchText}
              onCounts={({ unassigned, delay_days, delay_from }) => {
                setUnassignedCount(unassigned || 0);
                setDelayDays(delay_days || 0);
                setDelayFrom(delay_from || null);
              }}
            />
          </>
        ) : (
          <DemandeDetail
            demande={selectedDemande}
            onBack={() => setSelectedDemande(null)}
            onAssigned={() => {
              setSelectedDemande(null);
              refreshList();
            }}
          />
        )}
      </div>
    </div>
  );
}

function App() {
  const navigate = useNavigate();

  // ✅ login
  const [isLogged, setIsLogged] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");

  // ✅ mémorise l’utilisateur connecté (pour “Dernière modification : … — opérateur”)
  const [viewerName, setViewerName] = useState("");

  const allowedUsers = ["AMEL", "LAMIA", "CLAUDIA", "SOLENNE", "MORGAN", "ELISSA"];

  const goOperateur = (name) => {
    navigate(`/operateur/${encodeURIComponent(name)}`);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");

    const u = (loginUser || "").trim().toUpperCase();
    if (!allowedUsers.includes(u)) {
      setLoginError("Utilisateur inconnu.");
      return;
    }

    const expectedPass = u.charAt(0).toUpperCase() + "siao2026";
    if (loginPass !== expectedPass) {
      setLoginError("Mot de passe incorrect.");
      return;
    }

    setViewerName(u);
    setIsLogged(true);
    navigate("/");
  };

  // ✅ écran login avant l'app
  if (!isLogged) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          background: "#f5f6f8",
        }}
      >
        <form
          onSubmit={handleLogin}
          style={{
            width: "100%",
            maxWidth: 420,
            background: "white",
            border: "1px solid #e6e6e6",
            borderRadius: 14,
            padding: 18,
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: 6 }}>Connexion</h2>
          <p style={{ marginTop: 0, opacity: 0.8, marginBottom: 14 }}>
            Entrez votre nom d’utilisateur et votre mot de passe.
          </p>

          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <label style={{ fontWeight: 700 }}>Nom d’utilisateur</label>
              <input
                value={loginUser}
                onChange={(e) => setLoginUser(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #ddd",
                }}
              />
            </div>

            <div>
              <label style={{ fontWeight: 700 }}>Mot de passe</label>
              <input
                type="password"
                value={loginPass}
                onChange={(e) => setLoginPass(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: 6,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #ddd",
                }}
              />
            </div>

            {loginError ? (
              <div style={{ color: "red", fontWeight: 700 }}>{loginError}</div>
            ) : null}

            <button
              type="submit"
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                backgroundColor: "#111827",
                color: "white",
                fontWeight: 800,
              }}
            >
              Se connecter
            </button>
          </div>
        </form>

        {/* ✅ Logo sur l'écran de login aussi */}
        <SiteSignature />
      </div>
    );
  }

  return (
    <div className="main-layout">
      {/* ---- COLONNE GAUCHE ---- */}
      <div className="sidebar">
        <button
          onClick={() => goOperateur("AMEL")}
          style={{ backgroundColor: "red", color: "white" }}
        >
          AMEL
        </button>

        <button
          onClick={() => goOperateur("LAMIA")}
          style={{ backgroundColor: "#A020F0", color: "white" }}
        >
          LAMIA
        </button>

        <button
          onClick={() => goOperateur("CLAUDIA")}
          style={{ backgroundColor: "blue", color: "white" }}
        >
          CLAUDIA
        </button>

        <button
          onClick={() => goOperateur("SOLENNE")}
          style={{ backgroundColor: "orange", color: "white" }}
        >
          SOLENNE
        </button>

        <button
          onClick={() => goOperateur("MORGAN")}
          style={{ backgroundColor: "green", color: "white" }}
        >
          MORGAN
        </button>

        <div
          className="chef-box"
          style={{
            cursor: "pointer",
            backgroundColor: "pink",
            color: "white",
          }}
          onClick={() => goOperateur("ELISSA")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") goOperateur("ELISSA");
          }}
        >
          Cheffe de service
          <br />
          ELISSA
        </div>

        {/* ✅ bouton Accueil */}
        <button style={{ marginTop: 12 }} onClick={() => navigate("/")}>
          ACCUEIL
        </button>

        {/* ✅ STATS */}
        <button style={{ marginTop: 12 }} onClick={() => navigate("/stats")}>
          STATS
        </button>
      </div>

      {/* ---- ROUTES (contenu à droite) ---- */}
      <Routes>
        {/* ✅ IMPORTANT : on passe viewerName à Home (sinon "dernier import" ne sait pas qui importe) */}
        <Route path="/" element={<Home viewerName={viewerName} />} />
        <Route path="/stats" element={<StatsPage />} />

        <Route path="/operateur/:name" element={<OperatorMenu />} />
        <Route
          path="/operateur/:name/:item"
          element={<OperateurItemPage viewerName={viewerName} />}
        />
        <Route
          path="/operateur/:name/:item/:demandeId"
          element={<OperateurItemPage viewerName={viewerName} />}
        />
      </Routes>

      {/* ✅ Logo sur toutes les pages */}
      <SiteSignature />
    </div>
  );
}

export default App;
